import React from "react";
import startCase from "lodash/startCase";
import { useLocation } from "react-router";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, MenuItem, TextField } from "@mui/material";

import Can from "../../Policy/Can";
import { RootState } from "../../../store";
import { t } from "../../../components/translate";
import { Filters } from "../../../components/List";
import { IpdCases, IpdStatus } from "../../../constants/ipd";
import { useIpds } from "../../../hooks/query/useIpds";
import useQueryParams from "../../../hooks/useQueryParams";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { TWardType } from "../../../interfaces/WardInterface";
import IpdList, { pageSize } from "../IpdList";
import { TIpdCase, TIpdStatus } from "../../../interfaces/IpdInterface";
import { useWardTypeFromUrl } from "../../../hooks/useWardTypeFromUrl";
import { IpdAdmissionCreateEdit } from "../IpdAdmission/IpdAdmissionCreateEdit";
import { IpdSettingsPanel } from "../../ResourceCentre/ModuleSettings/IpdSettings/IpdSettingsPanel";

export enum IpdFilter {
  ALL = "all",
  ADMITTED = "admitted",
  DISCHARGED = "discharged"
}

// eslint-disable-next-line import/prefer-default-export
export function WardAdmissions({ wardType }: { wardType: TWardType }): React.ReactNode {
  const dispatch = useDispatch();
  const location = useLocation();

  // custom hooks
  const query = useQueryParams();
  const isMobileScreen = useMobileScreen();
  const wardTypeFromUrl = useWardTypeFromUrl();

  // selectors
  const policies = useSelector((state: RootState) => state.userContext.policies);

  // variables
  const ipdTabs = ["all", IpdStatus.ADMITTED, IpdStatus.DISCHARGED];
  const statusParam = query.get("status");

  const filterData = {
    filters: [
      { key: "all", title: `${t("all")}` },
      { key: IpdStatus.ADMITTED, title: `${t("admitted")}` },
      { key: IpdStatus.DISCHARGED, title: `${t("discharged")}` }
    ]
  };

  // local states
  const [page, setPage] = React.useState<number>(0);
  const [openCreateAdmissionForm, setOpenCreateAdmissionForm] = React.useState(false);
  const [selectedCase, setSelectedCase] = React.useState<TIpdCase | "all">("all");
  const [status, setStatus] = React.useState<"all" | TIpdStatus>(() => {
    const matched = ipdTabs.find((s) => s === statusParam);
    return matched ? (statusParam as TIpdStatus) : "all";
  });

  // effects
  React.useEffect(() => {
    setStatus((statusParam || IpdFilter.ALL) as "all" | TIpdStatus);
    setPage(0);
  }, [statusParam]);

  const { data, isLoading: isIpdsLoading } = useIpds({
    page,
    pageSize,
    status: status === "all" ? "" : status,
    currentCase: selectedCase === "all" ? undefined : selectedCase
  });

  const ipds = data?.data?.results ?? [];
  const totalIpdCount = data?.data?.total ?? 0;
  const resolvedCase = selectedCase !== "all" ? selectedCase : wardTypeFromUrl ?? wardType;

  return (
    <Box sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <Filters
          filter={status}
          onFilter={(newFilter) => {
            setStatus(newFilter as "all" | TIpdStatus);

            query.set("status", newFilter);
            dispatch(
              push({
                pathname: location.pathname,
                search: query.toString()
              })
            );
          }}
          filterData={filterData}
        />

        <Box sx={{ ml: 1, display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            select
            size="small"
            label="Current IPD Case"
            value={selectedCase}
            variant="outlined"
            onChange={(e) => {
              setSelectedCase(e.target.value as TIpdCase | "all");
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
            slotProps={{
              inputLabel: { shrink: true }
            }}
          >
            <MenuItem value="all">All Cases</MenuItem>
            {Object.values(IpdCases).map((type) => (
              <MenuItem key={type} value={type}>
                {startCase(type)}
              </MenuItem>
            ))}
          </TextField>

          {policies["ipd:createIpd"] && (
            <Button
              data-testmation="createIpd"
              variant="contained"
              onClick={() => setOpenCreateAdmissionForm(true)}
            >
              {isMobileScreen ? "Create" : `${t("createIPD")}`}
            </Button>
          )}

          <Can policyAccessKey="ipd:changeSettings">
            <IpdSettingsPanel />
          </Can>
        </Box>
      </Box>

      <IpdList
        data={ipds}
        totalIpdCount={totalIpdCount}
        isLoading={isIpdsLoading}
        page={page}
        onPageChange={setPage}
        defaultIpdCase={resolvedCase}
      />

      {openCreateAdmissionForm && (
        <IpdAdmissionCreateEdit
          defaultIpdCase={resolvedCase}
          onClose={() => setOpenCreateAdmissionForm(false)}
        />
      )}
    </Box>
  );
}
